<template>
  <CancellationModal
    :isOpen="isCancellationModalOpen"
    :accommodationName="cancellationAccommodationName"
    :accommodationId="cancellationAccommodationId"
    :type="cancellationProductType"
    @closeModal="closeCancellationModal"
  />

  <div>
    <BillingHeader
      title="tbs_product_overview"
      :accommodationsAmount="accommodationsData.length && !isPremiumProductsDataLoading"
    />
    <PremiumProductsFilters v-if="accommodationsData.length > 1" :accommodationsData="accommodationsData" />
    <div>
      <EmptyState
        v-if="
          accommodationsData.length === 1 && premiumProductsAccommodationsAmount === 0 && !isPremiumProductsDataLoading
        "
        title="tbs_product_empty_state_header"
        :text="isRateConnectEligible ? 'tbs_product_empty_state_upsell' : 'tbs_product_empty_state_plus_upsell'"
      />
      <EmptyState
        v-if="
          accommodationsData.length > 1 && premiumProductsAccommodationsAmount === 0 && !isPremiumProductsDataLoading
        "
        title="no_results"
        text="rc_booking_details_no_data_info"
        :class="{ 'tw:mt-6': accommodationsData.length < 1 }"
      />
      <div v-else>
        <MultipleItemsProductsContainer
          v-if="accommodationsData.length > 1 && premiumProductsAccommodationsAmount > 0"
          :data="premiumProductsData"
          :accommodationsData="accommodationsData"
        />
        <div
          v-else
          class="tw:flex tw:flex-col tw:gap-y-3 tw:mt-6 tw:mb-2"
          v-for="(item, index) in premiumProductsData[selectedAccommodationId]"
          :key="index"
        >
          <ProductItem :product="item" :accommodationName="currentAccommodation?.name" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, onUnmounted, watch } from 'vue'

import BillingHeader from '@/components/billing/BillingHeader.vue'
import PremiumProductsFilters from '@/components/billing/premiumProducts/PremiumProductsFilters.vue'
import ProductItem from '@/components/billing/premiumProducts/ProductItem.vue'
import MultipleItemsProductsContainer from '@/components/billing/premiumProducts/MultipleItemsProductsContainer.vue'
import EmptyState from '@/components/billing/EmptyState.vue'
import CancellationModal from '@/components/billing/premiumProducts/cancellation/CancellationModal.vue'

import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'
import { usePremiumProductsData } from '@/components/billing/premiumProducts/queries'
import { useStudioPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useStudioPlusReactivation'
import { usePremiumProductsFilters } from './usePremiumProductsFilters'
import { storage } from '@/utils/storageService'
import { useSlideLoader } from '@/components/base/loaders/useSlideLoader'
import { RC_BLOCKED_COUNTRIES } from '@/constants/rcBlockedCountries'
import { useCampaign, usePartners } from '@/components/rateConnect/queries'
import { useSessionValues } from '@/composables/useSessionValues'

const { selectedOrganisationId } = useSessionValues()
const { setLoadingState } = useSlideLoader()
const {
  isCancellationModalOpen,
  cancellationAccommodationName,
  cancellationAccommodationId,
  cancellationProductType,
  closeCancellationModal,
} = useStudioPlusReactivation()

const { selectedPropertyValues, selectedStatusValues } = usePremiumProductsFilters()
const propertyFilterValues = computed(() =>
  selectedPropertyValues.value.length === 1 && selectedPropertyValues.value[0].value === 'all'
    ? ''
    : selectedPropertyValues.value.map(item => item.value).join(',')
)

const rateConnectStatusValues = computed(() => {
  return selectedStatusValues.value
    .filter(item => item.product === 'rc')
    .map(item => item.value.toUpperCase())
    .join(',')
})

const plusStatusValues = computed(() => {
  return selectedStatusValues.value
    .filter(item => item.product === 'plus')
    .map(item => item.value.toUpperCase())
    .join(',')
})

const proStatusValues = computed(() => {
  return selectedStatusValues.value
    .filter(item => item.product === 'pro')
    .map(item => item.value.toUpperCase())
    .join(',')
})

const {
  premiumProductsData,
  premiumProductsAccommodationsAmount,
  isPremiumProductsDataLoading,
  isPremiumProductsDataFetching,
} = usePremiumProductsData({
  rateConnectStatuses: rateConnectStatusValues,
  plusStatuses: plusStatusValues,
  proStatuses: proStatusValues,
  accommodationIds: propertyFilterValues,
  pageNumber: 0,
  pageSize: 10000,
})
const { accommodationsData } = useAccommodationsByOrgId(selectedOrganisationId)
const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
const { campaignsData } = useCampaign(selectedAccommodationId)
const { isNetCpaEligible, activePartnersData } = usePartners(selectedAccommodationId)
const isEnabledForRateConnect = computed(() => (activePartnersData.value ?? []).length > 0)
const isBlockedCountry = computed(() => {
  const countryId = currentAccommodation.value?.countryId
  return countryId ? RC_BLOCKED_COUNTRIES.includes(countryId) : false
})
const isRateConnectEligible = computed(
  () => !campaignsData.value && isNetCpaEligible.value && isEnabledForRateConnect.value && !isBlockedCountry.value
)

watch(
  [() => isPremiumProductsDataLoading.value, () => isPremiumProductsDataFetching.value],
  ([isLoading, isFetching]) => {
    setLoadingState(isLoading || isFetching)
  },
  { immediate: true }
)

watch(
  () => premiumProductsData.value,
  newData => {
    if (newData) {
      const proSub = Object.values(newData)
        .filter(Array.isArray)
        .some(array => array.some(item => item?.productType === 'PRO'))

      storage.setItem('hasProSubscription', proSub)
    }
  },
  { once: true }
)

onUnmounted(() => setLoadingState(false))
</script>
